import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { toFollowupQueryParams, type FollowupQueryInput } from '@/modules/sentinel-workspace/followups/followup-query';
import type {
  FollowupDetail,
  FollowupOptOut,
  FollowupPage,
  FollowupReport,
  FollowupRow,
  TicketFollowupInput,
} from '@/modules/sentinel-workspace/followups/types';

interface ApiEnvelope<T> {
  data: T;
}

type BuScope = { bu_id?: string };

const { FOLLOWUPS } = API_ENDPOINTS.SENTINEL;

/**
 * Proactive follow-ups for the Sentinel Workspace. Reads and brand-level writes carry `bu_id`
 * like every other Sentinel list; a follow-up's own actions do not, because its id already
 * names one brand's wait (the same rule the ticket actions follow).
 */
export const followupsService = {
  async list(input: FollowupQueryInput, scope: BuScope = {}): Promise<FollowupPage> {
    const response = await api.get<ApiEnvelope<FollowupPage>>(FOLLOWUPS.LIST, {
      params: { ...toFollowupQueryParams(input), ...scope },
    });
    return response.data.data;
  },

  async get(id: string, scope: BuScope = {}): Promise<FollowupDetail> {
    const response = await api.get<ApiEnvelope<FollowupDetail>>(FOLLOWUPS.DETAIL(id), { params: scope });
    return response.data.data;
  },

  async cancel(id: string): Promise<FollowupRow> {
    const response = await api.post<ApiEnvelope<{ wait: FollowupRow }>>(FOLLOWUPS.CANCEL(id));
    return response.data.data.wait;
  },

  async send(id: string, text?: string): Promise<FollowupRow> {
    const response = await api.post<ApiEnvelope<{ wait: FollowupRow }>>(
      FOLLOWUPS.SEND(id),
      text === undefined ? {} : { text },
    );
    return response.data.data.wait;
  },

  /** The ticket's live follow-up, or null. */
  async getForTicket(ticketId: number): Promise<FollowupRow | null> {
    const response = await api.get<ApiEnvelope<{ followup: FollowupRow | null }>>(FOLLOWUPS.TICKET(ticketId));
    return response.data.data.followup;
  },

  /** Sets the ticket's follow-up, replacing a live one. */
  async setForTicket(ticketId: number, input: TicketFollowupInput): Promise<{ followup: FollowupRow; replaced: boolean }> {
    const response = await api.post<ApiEnvelope<{ followup: FollowupRow; replaced: boolean }>>(FOLLOWUPS.TICKET(ticketId), input);
    return response.data.data;
  },

  async listOptOuts(scope: BuScope = {}): Promise<FollowupOptOut[]> {
    const response = await api.get<ApiEnvelope<{ items: FollowupOptOut[] }>>(FOLLOWUPS.OPT_OUTS, { params: scope });
    return response.data.data.items;
  },

  async addOptOut(conversationId: number, scope: BuScope = {}): Promise<FollowupOptOut> {
    const response = await api.post<ApiEnvelope<{ item: FollowupOptOut }>>(withBuScope(FOLLOWUPS.OPT_OUTS, scope), {
      conversationId,
    });
    return response.data.data.item;
  },

  async removeOptOut(customerKey: string, scope: BuScope = {}): Promise<boolean> {
    const response = await api.delete<ApiEnvelope<{ removed: boolean }>>(withBuScope(FOLLOWUPS.OPT_OUT(customerKey), scope));
    return response.data.data.removed;
  },

  /** Last 7 days unless a window is given; the server defaults the same way. */
  async report(scope: BuScope = {}, window?: { from: string; to: string }): Promise<FollowupReport> {
    const response = await api.get<ApiEnvelope<FollowupReport>>(FOLLOWUPS.REPORT, { params: { ...scope, ...window } });
    return response.data.data;
  },
};
